import {
  Group,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  SRGBColorSpace,
  TextureLoader,
} from 'three';
import { scaleGround, setGridOpacity } from './ground.js';
import { createArenaWalls, createProps } from './obstacles.js';
import { createDistrictSetPieces } from './setpieces.js';

const tileGeometry = new PlaneGeometry(1, 1);
const atlasLoader = new TextureLoader();
let atlasTexture = null;
const tileMats = [
  new MeshBasicMaterial({ color: 0x102238, transparent: true, opacity: 0.10, depthWrite: false }),
  new MeshBasicMaterial({ color: 0x0b1829, transparent: true, opacity: 0.14, depthWrite: false }),
];

const sectorBlueprints = [
  { id: 'core', label: 'Core Block', floor: 0x102238, accent: 0x53ffb5, pattern: 'open', threat: 1.00, atlas: [0, 1] },
  { id: 'plaza', label: 'Neon Plaza', floor: 0x16264a, accent: 0x6aa9ff, pattern: 'rings', threat: 1.08, atlas: [1, 1] },
  { id: 'market', label: 'Dead Market', floor: 0x211a2f, accent: 0xff70d7, pattern: 'lanes', threat: 1.14, atlas: [0, 0] },
  { id: 'underpass', label: 'Underpass', floor: 0x1a2530, accent: 0xffd166, pattern: 'choke', threat: 1.20, atlas: [0, 1] },
  { id: 'reactor', label: 'Reactor Yard', floor: 0x0b3028, accent: 0x53ffb5, pattern: 'clusters', threat: 1.28, atlas: [1, 0] },
  { id: 'terminal', label: 'Terminal Edge', floor: 0x2b1022, accent: 0xff4f68, pattern: 'gauntlet', threat: 1.36, atlas: [1, 0] },
];

let mapState = null;

export function initGeneratedMap(world, options = {}) {
  world.mapRoot ||= new Group();
  world.mapRoot.name = 'generatedMapRoot';
  if (!world.mapRoot.parent) world.sceneRoot.add(world.mapRoot);

  mapState = {
    world,
    baseSize: world.size,
    squareSize: options.squareSize ?? 24,
    maxTier: options.maxTier ?? 5,
    autoExpand: options.autoExpand ?? false,
    game: options.game || null,
    tier: 0,
    sectors: [sectorBlueprints[0]],
    currentSector: sectorBlueprints[0],
    nextWave: 2,
    nextTime: options.firstExpansionTime ?? 10,
    cooldown: 0,
  };
  loadMapAtlas();
  world.generatedSquares = 1;
  if (mapState.game) {
    mapState.game.mapSize = world.size;
    mapState.game.generatedSquares = world.generatedSquares;
    mapState.game.mapSector = sectorBlueprints[0].label;
    mapState.game.sectorThreat = sectorBlueprints[0].threat;
  }
  renderMapSquares();
  setGridOpacity(0.22);
  createDistrictSetPieces(world);
  return mapState;
}

export function updateGeneratedMap(dt, player, game, callbacks = {}) {
  if (!mapState || game.mode !== 'playing') return false;
  if (!mapState.autoExpand) return false;
  mapState.cooldown = Math.max(0, mapState.cooldown - dt);
  if (mapState.cooldown > 0 || mapState.tier >= mapState.maxTier) return false;

  const edge = mapState.world.half - Math.max(Math.abs(player.pos.x), Math.abs(player.pos.z));
  const waveReady = (game.wave || 1) >= mapState.nextWave;
  const timeReady = (game.elapsed || 0) >= mapState.nextTime;
  if (edge > 10 && !waveReady && !timeReady) return false;

  const expanded = expandGeneratedMap(timeReady ? 'time' : waveReady ? 'wave' : 'edge');
  if (expanded) callbacks.onExpand?.(mapState);
  return expanded;
}

export function expandGeneratedMap(reason = 'edge') {
  if (!mapState || mapState.tier >= mapState.maxTier) return false;

  const { world } = mapState;
  const previousHalf = world.half;
  mapState.tier += 1;
  const sector = sectorBlueprints[mapState.tier % sectorBlueprints.length];
  mapState.currentSector = sector;
  mapState.sectors[mapState.tier] = sector;
  mapState.cooldown = 4;
  mapState.nextWave += 2;
  mapState.nextTime += 18;
  mapState.reason = reason;

  world.size = mapState.baseSize + mapState.squareSize * mapState.tier;
  world.half = world.size / 2;
  world.generatedSquares = 1 + mapState.tier;
  if (mapState.game) {
    mapState.game.mapSize = world.size;
    mapState.game.generatedSquares = world.generatedSquares;
    mapState.game.mapSector = sector.label;
    mapState.game.sectorThreat = sector.threat;
  }

  scaleGround(world);
  createArenaWalls(world);
  createProps(world, {
    seed: 19840508 + mapState.tier * 997,
    count: 14 + mapState.tier * 6,
    minAbs: previousHalf - 4,
    pattern: sector.pattern,
    accentColor: sector.accent,
  });
  renderMapSquares();
  return true;
}

function renderMapSquares() {
  const { world, squareSize } = mapState;
  world.mapRoot.clear();
  const start = -world.half + squareSize / 2;
  const count = Math.ceil(world.size / squareSize);

  for (let ix = 0; ix < count; ix += 1) {
    for (let iz = 0; iz < count; iz += 1) {
      const tile = new Mesh(tileGeometry, tileMats[(ix + iz) % tileMats.length]);
      const centerX = start + ix * squareSize;
      const centerZ = start + iz * squareSize;
      const ring = Math.max(0, Math.ceil((Math.max(Math.abs(centerX), Math.abs(centerZ)) - mapState.baseSize / 2) / squareSize));
      const sector = sectorAt(centerX, centerZ, ring);
      tile.material = makeTileMaterial(sector, ring, ix, iz);
      tile.rotation.x = -Math.PI / 2;
      tile.position.set(centerX, 0.003, centerZ);
      tile.scale.set(squareSize - 0.35, squareSize - 0.35, 1);
      tile.userData.generatedMapTile = true;
      tile.userData.sector = sector.id;
      world.mapRoot.add(tile);
    }
  }
}

function loadMapAtlas() {
  if (atlasTexture) return atlasTexture;
  atlasTexture = atlasLoader.load('/map/obsidian-floor-atlas.png');
  atlasTexture.colorSpace = SRGBColorSpace;
  return atlasTexture;
}

function sectorAt(x, z, ring) {
  if (ring > 0) return mapState.sectors[Math.min(ring, mapState.sectors.length - 1)] || mapState.currentSector;
  if (x >= 0 && z < 0) return sectorBlueprints[1];
  if (x < 0 && z >= 0) return sectorBlueprints[2];
  if (x >= 0 && z >= 0) return sectorBlueprints[4];
  return sectorBlueprints[0];
}

function makeTileMaterial(sector, ring, ix, iz) {
  const mat = tileMats[(ix + iz) % tileMats.length].clone();
  mat.color.setHex(sector.floor);
  mat.opacity = 0.42 + Math.min(0.12, ring * 0.018);
  if (!atlasTexture) return mat;
  const tex = atlasTexture.clone();
  tex.repeat.set(0.5, 0.5);
  tex.offset.set(sector.atlas[0] * 0.5, sector.atlas[1] * 0.5);
  tex.needsUpdate = true;
  mat.color.setHex(0xffffff);
  mat.opacity = 0.96;
  mat.map = tex;
  mat.needsUpdate = true;
  return mat;
}
